import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import axios from 'axios';
import { Upload, CheckCircle, Loader2, XCircle, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
    const [searchParams] = useSearchParams();
    const { refreshUser, user } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [status, setStatus] = useState('checking'); // checking, success, error
    const [attempts, setAttempts] = useState(0);
    const businessName = settings?.business_name || 'BGRemove';

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else {
            setStatus('error');
        }
    }, [searchParams]);

    const pollPaymentStatus = async (sessionId, currentAttempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (currentAttempts >= maxAttempts) {
            setStatus('error');
            return;
        }

        try {
            const response = await axios.get(`${API}/subscription/status/${sessionId}`);
            
            if (response.data.payment_status === 'paid') {
                setStatus('success');
                await refreshUser();
                return;
            } else if (response.data.status === 'expired') {
                setStatus('error');
                return;
            }

            // Continue polling
            setAttempts(currentAttempts + 1);
            setTimeout(() => pollPaymentStatus(sessionId, currentAttempts + 1), pollInterval);
        } catch (error) {
            console.error('Error checking payment status:', error);
            setAttempts(currentAttempts + 1);
            setTimeout(() => pollPaymentStatus(sessionId, currentAttempts + 1), pollInterval);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            <div className="max-w-md w-full text-center">
                <Link to="/" className="inline-flex items-center gap-2 mb-8" data-testid="success-logo-link">
                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                        <Upload className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                </Link>

                {status === 'checking' && (
                    <div className="glass-card rounded-2xl p-8" data-testid="checking-status">
                        <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
                        <h1 className="font-heading font-bold text-2xl mb-2">Processing Payment</h1>
                        <p className="text-muted-foreground">
                            Please wait while we confirm your payment...
                        </p>
                        {attempts > 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                                Attempt {attempts}/5
                            </p>
                        )}
                    </div>
                )}

                {status === 'success' && (
                    <div className="glass-card rounded-2xl p-8" data-testid="success-status">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Zap className="w-6 h-6 text-primary" />
                            <h1 className="font-heading font-bold text-2xl">Payment Successful!</h1>
                        </div>
                        <p className="text-muted-foreground mb-6">
                            {user?.subscription_plan
                                ? `Your subscription is now active! You now have ${user?.credits_available || 0} credits available.`
                                : `Your credits have been added! You now have ${user?.credits_available || 0} credits available.`
                            }
                        </p>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            className="rounded-full px-8"
                            data-testid="success-dashboard-btn"
                        >
                            Start Removing Backgrounds
                        </Button>
                    </div>
                )}

                {status === 'error' && (
                    <div className="glass-card rounded-2xl p-8" data-testid="error-status">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                            <XCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h1 className="font-heading font-bold text-2xl mb-2">Payment Issue</h1>
                        <p className="text-muted-foreground mb-6">
                            We couldn't confirm your payment. Please try again or contact support.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/pricing')}
                                className="rounded-full"
                                data-testid="error-retry-btn"
                            >
                                Try Again
                            </Button>
                            <Button 
                                onClick={() => navigate('/dashboard')}
                                className="rounded-full"
                                data-testid="error-dashboard-btn"
                            >
                                Go to Dashboard
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionSuccess;
